import React, { FC, useMemo, useState } from 'react';

import { formatNumber } from '@column/column-ui-kit';
import { DataTable, type DataTableSelectCellOption } from '@column/ui';

import {
  highlightMatch,
  renderMatchFragment,
} from '~/app/pages/Transfers/_components/AccountsSequence/AccountSelector';
import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';

interface BankAccountCellProps {
  isEditable?: boolean;
  value?: string;
  onChange?: ({ description, id }: { description: string; id: string }) => void;
}

const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'ACCOUNT_NUMBERS.ROUTING_NUMBER', label: 'RTN' },
  { field: 'ACCOUNT_NUMBERS.DESCRIPTION', label: 'ACCT DESC' },
  { field: 'ACCOUNT_NUMBERS.ACCOUNT_NUMBER_ID', label: 'ACCT ID' },
  { field: 'BANK_ACCOUNT_ID', label: 'ID' },
];

export const BankAccountCell: FC<BankAccountCellProps> = ({ value, onChange: onChangeProp, isEditable }) => {
  const {
    options: dropdownOptions,
    hiddenOptions,
    search,
    bankAccounts,
    handleScrollEnd,
  } = useBankAccountOptions({
    value,
  });
  const [query, setQuery] = useState('');

  const selectedLabel = useMemo(
    () =>
      value
        ? [...hiddenOptions, ...dropdownOptions].find((option) => String(option.value) === value)?.label
        : undefined,
    [dropdownOptions, hiddenOptions, value]
  );

  const options: DataTableSelectCellOption[] = useMemo(
    () =>
      bankAccounts.map((ba) => {
        const matchedHidden = getHighlightedSuffix(ba.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          value: ba.id,
          label: ba.display_name || ba.description || 'Unnamed',
          description: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : ba.default_account_number ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">ACCT </span>
              {highlightMatch(ba.default_account_number, query)}
            </span>
          ) : undefined,
          trailing: formatNumber(ba.balances?.available_amount ?? '', ba.currency_code),
          trailingDescription: 'Available',
          'data-cy': `dropdown-option-${ba.id}`,
        };
      }),
    [bankAccounts, query]
  );

  return (
    <DataTable.SelectCell
      isEditable={isEditable}
      isSearchable
      options={options}
      value={value}
      displayValue={selectedLabel}
      searchPlaceholder="Search bank accounts…"
      emptyMessage="No accounts found"
      onSearch={(searchValue) => {
        setQuery(searchValue);
        search(searchValue);
      }}
      onScrollEnd={handleScrollEnd}
      onValueChange={(newValue) => {
        const ba = bankAccounts.find((account) => account.id === newValue);
        onChangeProp?.({
          description: ba?.description ?? '',
          id: newValue,
        });
      }}
    />
  );
};
